import type { TurnBudget } from "./actionEconomy";
import type { Cell } from "./cell";
import type { CombatantId } from "./combatant";
import type { Side } from "./side";

/**
 * The **activation state machine** — the round, whose side acts next, the discriminated
 * turn phase, and the ledgers that outlive a single turn.
 *
 * The shape carries the invariant: `Turn` is a union, so "a budget exists but nobody is
 * acting" (or vice versa) is *unrepresentable* rather than an invariant policed by hand.
 * What belongs to one turn (the actor, its budget, its attack lock) lives inside the
 * `acting` arm; what outlives a turn (the acted set, the reaction ledgers, held actions)
 * is a sibling on {@link ActivationState}.
 */

/**
 * Flight's per-activation **direction lock** — the acting turn's optional committed
 * heading: the endpoints (`origin` → `dest`) of the FIRST flight `move_token` segment this
 * activation (or the first since a `boost` last cleared it). Set **once** and never
 * rewritten while it stands — a subsequent flight segment is checked against it (never
 * re-establishes it), re-projected from that segment's own origin via `world.translate` so
 * the same direction re-anchors wherever the mover currently stands.
 *
 * A single direction, not a per-grant budget stack: it answers only "may this segment
 * change heading?", never "how much of which grant is left?".
 */
export type FlightHeading = { origin: Cell; dest: Cell };

/**
 * The **turn** half of the activation machine. Exactly two phases:
 *   - `idle` — no turn is in progress (a fresh fight before the first activation, the gap
 *     between turns, or a round boundary). There is no per-turn budget.
 *   - `acting` — a unit is mid-turn: `actor` is the combatant whose turn it is, and
 *     `budget` is **its** per-turn action/move allotment. `heading` is the optional
 *     {@link FlightHeading} lock — present only once a Flying mover has committed to a
 *     direction this activation, absent otherwise (a fresh `activate()` never seeds it,
 *     and `boost` clears it by constructing a fresh `turn` object that simply omits it, so
 *     no explicit reset is needed).
 *
 * Per-round state that outlives a single turn (the acted set, the reaction ledger) is a
 * sibling on {@link ActivationState}, **not** in here.
 */
export type Turn =
  | { phase: "idle" }
  | { phase: "acting"; actor: CombatantId; budget: TurnBudget; attack?: AttackLock; heading?: FlightHeading };

/**
 * The **mid-attack lock** — the synced state that holds a sequential, multi-shot attack
 * action together as **one logical action** across its independently-committed shots. It
 * lives *inside* the `acting` arm of {@link Turn} (a sibling of `budget`), so it is
 * **unrepresentable when nobody is acting**. Being synced state it survives reconnect, and
 * every client sees "an attack is in progress."
 *
 * Its job is twofold: (1) **gate the acting actor's own further actions** — while it is
 * present, the actor may not make a standard move, Overcharge, or take a free action (RAW
 * Core p.62/p.70: you must complete the attack before taking other actions); (2) **record
 * which weapons have fired this action** so the next shot's legality and the auto-close
 * predicate ("no legal remaining attack") can read it. It blocks **only the acting actor's
 * own** actions — another actor's Brace/Overwatch reaction, triggered by a shot, resolves
 * normally between shots. **Exits:** fire the closing weapon (auto-close), an explicit
 * `end_attack`, or end turn (the next `activate`/`idle` drops it with the whole turn).
 *
 * Three consumers share the shape: a **Barrage** (1–2 shots), a **Skirmish's Auxiliary
 * rider** (at most a primary plus its one rider, opened only when the primary's mount
 * carries an eligible not-yet-fired Auxiliary — otherwise a Skirmish never opens a lock at
 * all), and **Full Tech** (its two sequential Quick Tech picks).
 */
export type AttackLock = {
  /**
   * Which RAW action opened this lock. Distinguishes the consumers' otherwise-identical
   * shape so the shared gates (opener-vs-continuation disambiguation, the mid-lock "finish
   * or end the attack" block) can tell them apart without a second field, and so the CLI
   * can report which action is in progress. Stamped once and never flipped mid-lock — a
   * Barrage never becomes a Skirmish or a Full Tech, or vice versa.
   */
  action: "skirmish" | "barrage" | "full_tech";
  /**
   * Barrage/Skirmish: the weapon **instance keys** fired so far this action, in fire order
   * (Barrage: one or two, a Superheavy fires alone; Skirmish: the primary, then its one
   * rider). Instance keys ({@link weaponInstanceKey}), NOT compendium ids — so two mounts
   * of the same weapon type each count once and neither blocks the other, while firing the
   * *same* physical copy twice is still rejected.
   *
   * **Full Tech** is a genuinely different vocabulary reusing the SAME field — the fired
   * Quick Tech **option ids**, in fire order. A mech's picks **may legitimately repeat**
   * (Bolster+Bolster pushes `["bolster","bolster"]`, RAW Core p.71's "may repeat the same
   * one"); `.length` drives the gating (0 → 1 → 2). An NPC's picks may not (Core p.280: "two
   * different Quick Tech actions"), so `checkFullTechContinuation` reads the contents for NPCs.
   */
  weapons: string[];
  /**
   * **Ordnance turn-virgin SNAPSHOT** — was `checkOrdnanceWindow` open at the moment this
   * lock OPENED? Captured once, on `open_attack` (never touched again by a continuation),
   * and read by every subsequent continuation/rider shot in place of re-deriving the window
   * live: a live re-check would always read `false` after the opener's own action spent its
   * point(s), wrongly rejecting every continuation of an Ordnance-opened attack.
   *
   * **A snapshot, not a delta exemption**, deliberately: the lock's shot count varies, and
   * an exact-delta comparison would silently rot as it grows, while this snapshot stays
   * honest by construction. A lock whose opener weapon isn't Ordnance never reads it.
   */
  ordnanceWindow?: boolean;
  /**
   * Barrage/Skirmish: the **committed mount keys** (`mountKeyOf` in `@lancer-vtt/rules`:
   * `mount:<i>` for `base.mounts[i]`, or a weapon's own instance key when it sits on no frozen
   * mount), named by the opener (a Skirmish lock holds its first weapon's one mount). Every
   * shot this action must fire a weapon on one of these mounts, and the lock
   * auto-closes once no unfired weapon on them can fire (`decisions/0049`'s 2026-09-24
   * amendment). A Skirmish lock holds its one mount. Absent on a Full Tech lock.
   */
  mounts?: string[];
}

/**
 * A **held (prepared) action** — the record `prepare` (RAW Core pp. 71–72) leaves on its
 * preparer: as a quick action, ready any *other* quick action with a freeform "when X, then
 * Y" trigger, and hold it until the trigger fires (a human releases it) or it lapses at the
 * preparer's own next activation.
 *
 * **Captured at prepare, bound at release**: RAW's own "ram *them*" example shows the
 * concrete target is trigger-determined and unknowable at prepare time, so only what the
 * prepare-time restrictions themselves need is captured here. The target and dice are
 * supplied later, at `release_prepare`.
 */
export type HeldAction = {
  /**
   * The RAW-catalog action id being readied (`"skirmish"`, `"ram"`, `"grapple"`,
   * `"improvised_attack"`, `"quick_tech"`) — **attack-shaped ids only**, because preparable
   * must equal releasable: only the ids `isAttackShapedHeld` accepts may be readied. This
   * is the id `prepare`'s once-per-turn ledger stamp records — NEVER a `"prepare"` id of
   * its own, since a second Prepare is blocked by the lockout rather than by a `"prepare"`
   * cap.
   */
  readiedActionId: string;
  /**
   * The named weapon id off the readied action — `prepare`'s Ordnance-window/Loading gates
   * read this exactly like a live attack intent's own `weapon` field. **Required for
   * `"skirmish"` and `"quick_tech"`; optional (and typically absent) for the
   * synthetic-weapon verbs** (`"ram"`/`"grapple"`/`"improvised_attack"`, whose `act_*`
   * weapon is implied by the verb). `checkPreparableHold` enforces this:
   *   - `"skirmish"` MUST name a real loadout weapon — its weapon is a free variable at
   *     release (any plain weapon maps to `"skirmish"`), so a WEAPONLESS Skirmish hold
   *     would skip the prepare-time Ordnance/Loading gate AND the release-time
   *     weapon-match, letting an Ordnance shot be armed out-of-turn with its window checked
   *     nowhere. Requiring the weapon at prepare closes that bypass.
   *   - `"quick_tech"` MUST be `"act_invade"` — the only weapon that makes it the
   *     attack-shaped Invade flavor (the non-attack Scan/Lock On/Bolster flavor is not
   *     releasable).
   */
  weapon?: string;
  /** The named mount id off the readied action, when relevant — a display/authoring breadcrumb, not read by any prepare-time gate. */
  mount?: string;
  /** The freeform "when X, then Y" trigger text — human-released, not a bounded predicate. */
  triggerText: string;
}

/**
 * **A watcher's standing "no" to one mover's movement reactions** — one entry per
 * `(watcherId, moverId, consumerId)`. Recorded by `record_move_reaction_decline` when a
 * watcher (or the GM backstop) answers a movement-checkpoint offer with "decline", and read by
 * the checkpoint compilers (`@lancer-vtt/rules`' `hasLiveMoveReactionDecline`) to skip that
 * watcher for the rest of the mover's turn (developer ruling 2026-09-24,
 * `projects/overwatch-mid-move.md` Q2 = (ii)).
 *
 * RAW makes every movement that starts in threat a fresh Overwatch trigger, but Overwatch is
 * optional, so skipping the offer takes no right away: a watcher who changes their mind can
 * still fire manual Overwatch. **A decline is not a spent reaction** — it never touches
 * `reactionSlot` or `reactionUses`.
 *
 * `consumerId` is the movement-checkpoint consumer the offer came from (`"overwatch"` or
 * `"authored_move_reaction"`), so declining one reaction does not silence a different one the
 * same watcher holds against the same mover.
 */
export type MoveReactionDecline = {
  watcherId: CombatantId;
  moverId: CombatantId;
  consumerId: string;
};

/**
 * **A movement Covering Fire split in two** (`landing-zone-full-automation` Q5, Core p.97: "You can
 * make this attack at any point during their movement"). The shooter picked a cell on the declared
 * path; the move committed up to it and now holds there for the shot. The rest of the path commits
 * as the SAME movement when the held move is re-sent, so anything that counts "a movement" reads
 * this record rather than the command: start-of-move Overwatch and `on_move` fire once (the
 * remainder skips the gather and fires `on_move` with the whole path), and the Trample sweep is
 * shared (Q4).
 *
 * Recorded by `set_split_move` in the prefix's plan, cleared by the same command in the
 * remainder's plan, dropped at the mover's `end_turn`, and absent from a fresh round's activation.
 */
export type SplitMove = {
  moverId: CombatantId;
  /** Who picked the cell, and who the "take the shot now" prompt goes to. */
  shooterId: CombatantId;
  /** The whole declared anchor path, start to end. */
  path: Cell[];
  /** Index into {@link path} of the cell the mover holds on. */
  at: number;
  /**
   * The characters the first half trampled when it spent the turn's Trample sweep, so the
   * remainder damages whoever else the movement crosses without spending it again. Absent when
   * the first half dealt no Trample damage.
   */
  trampled?: CombatantId[];
};

/**
 * The **RAW activation state** of a round. Owns the round counter, whose side acts next,
 * who has fully ended their turn, the two reaction ledgers, and the discriminated
 * {@link Turn}. The un-acted pool is *derived* from this plus the combatant roster, never
 * stored as a rigid order. JSON-shaped (no class, no `Set`) — travels the wire and persists
 * like the rest of the encounter.
 */
export type ActivationState = {
  /** Round counter, starting at 1. Incremented by `endRound`. */
  round: number;
  /**
   * **Monotonic turn identity** — a global counter starting at `0`, bumped by every
   * `activate` call and never reset by `endRound` (carried forward like `reactionSlot` and
   * `heldActions`). Stamped as a scalar onto
   * {@link StatusInstance.appliedTurn}/{@link AreaEffect.appliedTurn} at creation, so the
   * duration bus can tell "the turn this was applied on" apart from "a later turn of the
   * same combatant".
   *
   * A scalar rather than a `{round, combatantId}` pair (which collapses two of a
   * multi-activation combatant's turns in one round to the same identity) or a
   * `{round, combatantId, activation}` triple (which forces every reader to know
   * `activationsUsed`'s increment-at-`endTurn` off-by-one). **Global rather than
   * per-combatant** because the projection scrubs per-combatant ledgers for concealed ids —
   * a global scalar carries no per-combatant information, so there is nothing for that
   * scrub to get wrong.
   */
  turnOrdinal: number;
  /**
   * The side whose turn it is to activate **next**. The round opens on the side that did
   * *not* take the last turn (a fresh fight opens `"pc"`). `endTurn` flips it to the other
   * side, unless that side has no able un-acted units left (the uneven-side consecutive
   * case), in which case it stays on the side that still has units. `activate` does **not**
   * flip it.
   */
  activeSide: Side;
  /**
   * Combatant ids that **have fully ended their turn** this round (post-`endTurn`). A set,
   * stored as an array for JSON and order stability — append order is the turn order, which
   * `endRound` and the opening-side helper read. `endRound` clears it; a mid-round-added
   * unit is absent ⇒ un-acted; a destroyed unit drops out of the *derived* pool, so its
   * presence here is harmless. Deduped by the reducer.
   */
  activated: CombatantId[];
  /**
   * The **global reaction slot** — one per combatant, keyed by combatant id (RAW Core
   * pp. 61, 73: a character may take at most one reaction per turn — "your turn or other
   * characters' turns"). Consumed when the combatant spends ANY reaction; cleared to `{}` (everyone
   * open) by every `activate`, anyone's, **not** by `endRound` (`decisions/0056`). A sibling of
   * `activated` rather than a field inside `turn`, because a reaction is spent *out of turn* by a
   * combatant other than the active one, and one spent in an idle gap must stay spent until the
   * next turn starts.
   *
   * A missing key reads as the default allotment ({@link REACTION_SLOT_ALLOWANCE}). Carries
   * forward across `endRound` UNCHANGED. Plain `Record`, no `Map`.
   */
  reactionSlot: Record<CombatantId, number>;
  /**
   * The **per-named-reaction, per-combatant cap** — how many times a combatant has used a
   * given named reaction (`"overwatch"` | `"brace"`) since its own last turn start, independent
   * of how many times its {@link reactionSlot} has reopened. The slot reopens every turn, so
   * this is what keeps a unit from Overwatching on two different turns before its own.
   *
   * Mirrors `TurnBudget.actionUses` — same `Record<string, number>` shape and default-1
   * allowance discipline (see {@link canUseReaction}) — keyed per-combatant since it isn't
   * nested inside a per-turn budget. A combatant's entry is cleared by its OWN `activate` (Core
   * p.73: "your uses of it only refresh when your next turn begins"; developer ruling
   * 2026-10-10, `decisions/0056`); `endRound` carries actors' entries forward and drops
   * non-actors' (an object never takes a turn). A missing combatant or reaction id reads as
   * unused.
   */
  reactionUses: Record<CombatantId, Record<string, number>>;
  /**
   * The **per-round action-use ledger** — how many times each combatant has used a given
   * `1/round`-frequency action id this round. A structural sibling of {@link reactionUses}
   * (same shape, same consumed-count polarity — see {@link canUseRoundAction}), keyed
   * per-combatant for the identical reason: a multi-activation combatant's round cap must
   * survive across its own multiple activations even though its turn budget resets every
   * activation.
   *
   * Reset to `{}` for everyone by `endRound` — never carried forward, unlike
   * `reactionSlot`. A missing combatant or action id reads as unused (0 consumed, the
   * sparse-absent convention). This is one of the three per-cadence substrates unified at a
   * single read predicate (`checkActionAvailable`, `@lancer-vtt/rules`' `economy.ts`) — **no
   * other read site may consult this map directly.**
   */
  actionRoundUses: Record<CombatantId, Record<string, number>>;
  /** The discriminated turn phase — `idle` or `acting`-with-budget. See {@link Turn}. */
  turn: Turn;
  /**
   * **Held (prepared) actions**, keyed by the preparing combatant — at most one entry each.
   * A **top-level sibling of `reactionSlot`/`reactionUses`, deliberately NOT inside the
   * `acting` arm of {@link Turn}**: unlike {@link AttackLock} (strictly within-turn), a held
   * action OUTLIVES the preparer's own turn — it persists through the idle gap and every
   * other combatant's turns until the trigger fires or the preparer next activates.
   *
   * Presence of an entry is the **lockout**: while `heldActions[id]` exists, `id` may not
   * move, take any action, or take a reaction — the RAW price of holding. Cleared by
   * `drop_prepare` (voluntary release), `release_prepare` (the trigger fires), or the lapse
   * at the preparer's own next `activate` (untriggered = lost). Synced state, which is what
   * gives the table-visible telegraph for free.
   */
  heldActions: Record<CombatantId, HeldAction>;
  /**
   * **Round-scoped activation tally** — how many turns each combatant has taken so far THIS
   * round. The gate `endTurn` reads to decide whether a just-ended unit is fully done
   * (`activated`) or has another turn coming. A missing key reads as `0`: a fresh combatant,
   * or one that hasn't yet activated this round, has used none of its activations.
   *
   * **A fresh-`{}`-per-round tally, NOT carried forward like `reactionSlot`/`heldActions`.**
   * `endRound` mints the next state via {@link freshActivation}, which mints this `{}`, and
   * unlike those two does NOT splice a prior value back in — so a combatant that took its
   * 2nd turn on round N owes one turn again on round N+1, not zero.
   */
  activationsUsed: Record<CombatantId, number>;
  /**
   * **Movement-reaction declines still in force** — see {@link MoveReactionDecline}. Absent or
   * empty means nobody has declined anything. Entries for a mover are dropped at that mover's
   * own `end_turn`; `endRound` mints a fresh activation without this field, which clears any
   * entry left over (a mover that moved off its own turn) as a backstop. Deduped by the
   * reducer, append order otherwise.
   */
  moveReactionDeclines?: MoveReactionDecline[];
  /** The movement held partway for a Covering Fire shot — see {@link SplitMove}. Absent when none is. */
  splitMove?: SplitMove;
}

/**
 * The combatant **currently mid-turn**, or `null` when {@link Turn} is `idle`. Every "who
 * is acting?" question goes through here (or directly through `turn.phase`).
 */
export function activeActor(activation: ActivationState): CombatantId | null {
  return activation.turn.phase === "acting" ? activation.turn.actor : null;
}

/**
 * The **mid-attack lock currently in effect**, or `null` when no attack is in progress. The
 * lock lives inside the `acting` arm of {@link Turn}, so this is `null` whenever the turn
 * is `idle` *or* the acting actor has no open lock. The acting actor it belongs to is
 * `activation.turn.actor` (read via {@link activeActor}); callers gate that actor's own
 * move / Overcharge / free actions on this being `null`.
 */
export function activeAttack(activation: ActivationState): AttackLock | null {
  return activation.turn.phase === "acting" ? activation.turn.attack ?? null : null;
}

/**
 * The **held (prepared) action** currently on `id`, or `null` if it isn't holding one — the
 * one read every lockout choke point (move, action, reaction) and the release path consult.
 * A thin lookup (unlike {@link activeAttack}, `heldActions` is already a flat top-level map)
 * kept as its own named accessor so every consumer reads it the same way and a future
 * reshape has one call site to update.
 */
export function heldAction(activation: ActivationState, id: CombatantId): HeldAction | null {
  return activation.heldActions[id] ?? null;
}

/**
 * The **held-action lockout's rejection sentence** for `id`, naming the verb it is holding.
 *
 * One builder, two readers, deliberately: the room's gate-ladder arm 1
 * (`backend/src/encounterGateLadder.ts` `checkHeldActionLockout`) turns it into the
 * `{reason, category}` the refusing client receives, and the browser's action bar
 * (`frontend/src/lib/encounter/actionStatusMirror.ts` `heldLockoutBlockReason`) renders the same
 * sentence as every row's title while the hold is armed. **The affordance and the refusal must be
 * the same words** — a player who reads "you can't, because X" on a greyed row and then a
 * different X in the rejection toast learns to distrust both. Lives here, next to
 * {@link heldAction}, because that is the one read both sides already make.
 *
 * `backend/src/encounterGatePrecedence.pin.test.ts` pins the exact string twice; changing the
 * wording is an observable behavior change, not a copy edit.
 */
export function heldLockoutReason(id: CombatantId, held: HeldAction): string {
  return `${id} is holding a prepared ${held.readiedActionId} — drop it (drop_prepare) or wait for it to trigger/lapse before acting`;
}

/**
 * A fresh activation state opening round `round` (default 1) on `opening` (default `"pc"`
 * — a brand-new fight goes players-first). Nobody is mid-turn, nobody has acted.
 *
 * `reactions.slot` (default empty — everyone lazily reads as having their reaction) and
 * `reactions.uses` (default empty) are the round's two reaction ledgers, bundled into one
 * options param to stay under the 3-parameter lint cap. `endRound` passes the opening side,
 * the bumped round, and the prior `reactionSlot` **carried forward unchanged** — it is not a
 * round-boundary concern, only `activate` reopens it — and the actors' `uses` entries (only
 * each actor's own `activate` refreshes them). `emptyEncounter`/migration call with no args.
 *
 * **`turnOrdinal` always starts at `0` here**, but like `heldActions` it is NOT a
 * round-boundary concern (unlike `activated`/`actionRoundUses`, which genuinely reset), so
 * `endRound` splices the prior value back in after calling this. Only a
 * genuinely fresh encounter keeps the `0` this function mints.
 */
export function freshActivation(
  opening: Side = "pc",
  round = 1,
  reactions: {
    slot?: Record<CombatantId, number>;
    uses?: Record<CombatantId, Record<string, number>>;
  } = {},
): ActivationState {
  return {
    round,
    activeSide: opening,
    activated: [],
    reactionSlot: reactions.slot ?? {},
    reactionUses: reactions.uses ?? {},
    // The round-scoped action-use ledger is NEVER carried forward (unlike `reactionSlot`
    // above) — every fresh activation, `endRound`'s included, starts it empty. No options
    // leg needed: nothing legitimately wants a non-empty starting ledger.
    actionRoundUses: {},
    turn: { phase: "idle" },
    heldActions: {},
    activationsUsed: {},
    turnOrdinal: 0,
  };
}
